import { api } from '../utils/api';

export interface ComplianceDocument {
  id: string;
  type: string;
  fileName: string;
  fileUrl: string;
  uploadedAt: string;
  status: 'pending' | 'approved' | 'rejected';
}

export interface ComplianceSubmission {
  id: string;
  organizationId: string;
  status: 'draft' | 'pending' | 'in_review' | 'approved' | 'rejected' | 'expired';
  country: string;
  phoneNumbers: string[];
  requirementGroupId?: string;
  documents: ComplianceDocument[];
  submittedAt?: string;
  approvedAt?: string;
  expiresAt?: string;
  rejectionReason?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface ComplianceRequirement {
  type: string;
  description: string;
  required: boolean;
  documentTypes: string[];
}

export interface SubmitComplianceRequest {
  phoneNumbers: string[];
  country: string;
  businessName: string;
  businessAddress: string;
  businessType: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  documents: Array<{
    type: string;
    fileId: string;
  }>;
}

export interface UpdateComplianceRequest {
  status?: string;
  documents?: Array<{
    type: string;
    fileId: string;
  }>;
  metadata?: Record<string, any>;
}

export const regulatoryComplianceService = {
  /**
   * Get compliance status for an organization
   */
  async getStatus(organizationId: string, params?: { status?: string; country?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.country) queryParams.append('country', params.country);

    const url = `/regulatory-compliance/${organizationId}/compliance${
      queryParams.toString() ? `?${queryParams}` : ''
    }`;

    const response = await api.get<{ submissions: ComplianceSubmission[] }>(url);
    return response.data;
  },

  /**
   * Get a specific compliance submission
   */
  async getSubmission(organizationId: string, complianceId: string) {
    const response = await api.get<{ submission: ComplianceSubmission }>(
      `/regulatory-compliance/${organizationId}/compliance/${complianceId}`
    );
    return response.data;
  },

  /**
   * Submit a new compliance request
   */
  async submitRequest(organizationId: string, data: SubmitComplianceRequest) {
    const response = await api.post<{ submission: ComplianceSubmission }>(
      `/regulatory-compliance/${organizationId}/submit`,
      data
    );
    return response.data;
  },

  /**
   * Update an existing compliance submission
   */
  async updateSubmission(
    organizationId: string,
    complianceId: string,
    data: UpdateComplianceRequest
  ) {
    const response = await api.put<{ submission: ComplianceSubmission }>(
      `/regulatory-compliance/${organizationId}/compliance/${complianceId}`,
      data
    );
    return response.data;
  },

  /**
   * Get all documents for a compliance submission
   */
  async getDocuments(organizationId: string, complianceId: string) {
    const response = await api.get<{ documents: ComplianceDocument[] }>(
      `/regulatory-compliance/${organizationId}/compliance/${complianceId}/documents`
    );
    return response.data;
  },

  /**
   * Delete a document from a compliance submission
   */
  async deleteDocument(organizationId: string, complianceId: string, documentId: string) {
    const response = await api.delete(
      `/regulatory-compliance/${organizationId}/compliance/${complianceId}/documents/${documentId}`
    );
    return response.data;
  },

  /**
   * Upload a file for compliance documentation
   */
  async uploadFile(file: File, metadata?: Record<string, any>) {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata) {
      formData.append('metadata', JSON.stringify(metadata));
    }

    const response = await api.post<{ fileId: string; fileUrl: string; fileName: string }>(
      '/files/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Get compliance requirements for a country
   */
  getRequirementsForCountry(country: string): ComplianceRequirement[] {
    const requirements: Record<string, ComplianceRequirement[]> = {
      AU: [
        {
          type: 'Business Registration',
          description: 'Proof of business registration (ABN/ACN)',
          required: true,
          documentTypes: ['Business Registration Certificate', 'ABN Registration'],
        },
        {
          type: 'Business Address',
          description: 'Proof of business address in Australia',
          required: true,
          documentTypes: ['Utility Bill', 'Lease Agreement', 'Bank Statement'],
        },
        {
          type: 'Identity Verification',
          description: 'Government-issued ID of authorized person',
          required: true,
          documentTypes: ['Drivers License', 'Passport'],
        },
        {
          type: 'Letter of Authorization',
          description: 'Signed authorization for phone number usage',
          required: true,
          documentTypes: ['LOA'],
        },
      ],
      US: [
        {
          type: '10DLC Registration',
          description: 'Campaign Registry brand registration',
          required: true,
          documentTypes: ['Business Registration', 'Tax ID'],
        },
      ],
    };

    return requirements[country] || [];
  },
};
